import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ScrollEventData } from 'react-virtualized'
import { defaultRowRenderer, TableRowRenderer } from 'react-virtualized/dist/es/Table'
import { memoChecker } from '../../utils/globals/components'
import { twinFetchPostJSON } from '../../utils/globals/data'
import { TwinDictionary } from '../../utils/globals/dictionary'
import { createLink, CreateLinkFunctionType, CreateLinkProps } from '../../utils/globals/link'
import useConfigColumns, { SelectedColumnsAndFilters } from '../../utils/hooks/useConfigColumns'
import useTwinTranslation from '../../utils/hooks/useTwinTranslation'
import { ButtonConfigColumnsWithModalBase, ResetButtonConfigColumns } from '../ModalsLayouts/ModalConfigColumnsTabs'
import { buildOrder, buildWhereBySearchbars, getNextOrder, getWhereFromGeneralHeaders, saveTableData } from './functions'
import { DefaultOrder, ParseDataFunc, SetTableInstance, TableConfiguration } from './types'
import { VirtualTableListingStateLess } from './VirtualTableListing'
import { SearchBarTable } from './VirtualTableListing/Subcomponents'
import { ColumnConfiguration } from './VirtualTableListing/Subcomponents/types'
import { OnRowClick } from './VirtualTableListing/types'
import LoaderDecider from '../LoaderDecider'
import { getSingleEmployeeParam } from '../../utils/reducers/getters'
import { changeEmployeeParams } from '../../utils/reducers/reduxDispatch'
import { flushSync } from 'react-dom'
import { InputCalendarStateFull } from '../../forms/Input/InputCalendar'

type TableProps = TableLogicProps & {
    children?: React.ReactNode
    className?: string
    name: string
    hideLoadFiltersButton?: boolean
    hideChangeColumnsButton?: boolean
}

const DEFAULT_ROWHEIGHT_PX = 48
const DEFAULT_LIMIT_ROWS = 30

const TwinTable: React.FC<TableProps> = memo(({ name, columns, getDataFrom, defaultOrder, extraWhereParams, rowRenderer, setTableInstance, onRowClick, children, parseData, maxOffset, hideLoadFiltersButton, hideChangeColumnsButton, className, extraWhereSubstituibleParams }) => {
    const { selectedColumnsAndFilters, setSelectedColumnsAndFilters, memorizedColumns } = useConfigColumns({ columns, name })
    const { tableData, tableConfiguration, changeGeneralSearchbar, headerChanges, changeOffsetVirtualTable, setTableConfiguration, saveTableDefaultFilterWhenChanges, getTableDataFromStart, changeGeneralHeaders } = useTableLogic({ columns, getDataFrom, defaultOrder, extraWhereParams, setTableInstance, parseData, maxOffset, filters: selectedColumnsAndFilters.filters, order: selectedColumnsAndFilters.order, extraWhereSubstituibleParams, name, generalSearchbar: selectedColumnsAndFilters.generalSearchbar, setSelectedColumnsAndFilters, generalHeaders: selectedColumnsAndFilters.generalHeaders })
    const { t } = useTwinTranslation()
    return (
        <div className={'flex flex-col flex-auto h-1 ' + (className || '')}>
            <div className='table_header flex justify-between items-center mb-38 flex-wrap gap-10 gap-y-20'>
                <div className='flex items-end gap-x-15 gap-y-20 flex-auto w-1 flex-wrap'>
                    <div className='table_searcher_header'>
                        <SearchBarTable onChange={changeGeneralSearchbar} placeholder={t('searchDots', 'Buscar...')} value={selectedColumnsAndFilters.generalSearchbar || ''} className='search_bar_table' />
                    </div>
                    <div className='flex items-end gap-x-15 gap-y-20 flex-wrap table_general_headers'>
                        <InputCalendarStateFull label={t('createdFrom', 'Creado Desde')} onlyValids={true} className='product_table_filters min-w-200' onChange={(value) => changeGeneralHeaders('start_createdAt', value)} value={selectedColumnsAndFilters?.generalHeaders?.start_createdAt} allowEmptyEffect={true} />
                        <InputCalendarStateFull label={t('createdTo', 'Creado Hasta')} onlyValids={true} className='product_table_filters min-w-200' onChange={(value) => changeGeneralHeaders('end_createdAt', value)} value={selectedColumnsAndFilters?.generalHeaders?.end_createdAt}  allowEmptyEffect={true}/>
                    </div>
                </div>
                <div className='right_side grid_header_table items-center flex flex-wrap gap-x-15 gap-y-10'>
                    {!hideChangeColumnsButton ?
                        <ResetButtonConfigColumns name={name} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} selectedColumnsAndFilters={selectedColumnsAndFilters} setTableConfiguration={setTableConfiguration} getTableDataFromStart={getTableDataFromStart} />
                        : null
                    }
                    {children}
                    {!hideChangeColumnsButton ?
                        <ButtonConfigColumnsWithModalBase name={name} columns={columns} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} selectedColumnsAndFilters={selectedColumnsAndFilters} hideLoadFiltersButton={hideLoadFiltersButton} setTableConfiguration={setTableConfiguration} getTableDataFromStart={getTableDataFromStart} />
                        : null
                    }
                </div>
            </div>
            <div className='flex-auto'>
                <LoaderDecider loading={tableData === null ? true : false}>
                    <VirtualTableListingStateLess {...{ columns: memorizedColumns, tableConfiguration, headerChanges, rowRenderer, onRowClick }} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} tableData={tableData} onScroll={changeOffsetVirtualTable} rowHeight={DEFAULT_ROWHEIGHT_PX} headerHeight={48} name={name} selectedFilters={selectedColumnsAndFilters.filters} selectedOrder={selectedColumnsAndFilters.order} selectedFilterId={selectedColumnsAndFilters.id} saveTableDefaultFilterWhenChanges={saveTableDefaultFilterWhenChanges} />
                </LoaderDecider>
            </div>
        </div>
    )
}, (oldProps, nextProps) => memoChecker(oldProps, nextProps, ['columns', 'defaultOrder', 'extraWhereParams', 'extraWhereSubstituibleParams', 'getDataFrom', 'setTableInstance', 'rowRenderer', 'children']))

export const TwinSimpleTable: React.FC<TableProps> = memo(({ columns, getDataFrom, defaultOrder, extraWhereParams, extraWhereSubstituibleParams, rowRenderer, setTableInstance, onRowClick, parseData, maxOffset, name }) => {
    const { selectedColumnsAndFilters, setSelectedColumnsAndFilters, memorizedColumns } = useConfigColumns({ columns, name })
    const { tableData, tableConfiguration, headerChanges, changeOffsetVirtualTable, saveTableDefaultFilterWhenChanges } = useTableLogic({ columns, getDataFrom, defaultOrder, extraWhereParams, setTableInstance, parseData, maxOffset, filters: selectedColumnsAndFilters.filters, order: selectedColumnsAndFilters.order, extraWhereSubstituibleParams, name, generalSearchbar: selectedColumnsAndFilters.generalSearchbar, setSelectedColumnsAndFilters, generalHeaders: selectedColumnsAndFilters.generalHeaders })
    return (
        <div className={'flex flex-col flex-auto h-1'}>
            <div className='flex-auto'>
                <VirtualTableListingStateLess {...{ columns: memorizedColumns, tableConfiguration, headerChanges, rowRenderer, onRowClick }} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} tableData={tableData} onScroll={changeOffsetVirtualTable} rowHeight={DEFAULT_ROWHEIGHT_PX} headerHeight={48} name={name} selectedFilters={selectedColumnsAndFilters.filters} selectedOrder={selectedColumnsAndFilters.order} selectedFilterId={selectedColumnsAndFilters.id} saveTableDefaultFilterWhenChanges={saveTableDefaultFilterWhenChanges} />
            </div>
        </div>
    )
}, (oldProps, nextProps) => memoChecker(oldProps, nextProps, ['columns', 'defaultOrder', 'extraWhereParams', 'extraWhereSubstituibleParams', 'getDataFrom', 'setTableInstance', 'rowRenderer']))

interface TableLogicProps {
    name?: string
    columns: ColumnConfiguration[]
    getDataFrom: string
    defaultOrder?: DefaultOrder
    extraWhereSubstituibleParams?: TwinDictionary
    extraWhereParams?: TwinDictionary
    rowRenderer?: TableRowRenderer
    onRowClick?: OnRowClick
    setTableInstance?: SetTableInstance
    parseData?: ParseDataFunc
    maxOffset?: number
    filters?: SelectedColumnsAndFilters['filters']
    order?: SelectedColumnsAndFilters['order']
    generalSearchbar?: SelectedColumnsAndFilters['generalSearchbar']
    generalHeaders?: SelectedColumnsAndFilters['generalHeaders']
    setSelectedColumnsAndFilters?: React.Dispatch<React.SetStateAction<SelectedColumnsAndFilters>>
}

export const useTableLogic = ({ setTableInstance, defaultOrder, getDataFrom, columns, extraWhereParams, parseData, maxOffset, filters, order, generalSearchbar, generalHeaders, extraWhereSubstituibleParams, name, setSelectedColumnsAndFilters }: TableLogicProps) => {
    const [tableConfiguration, setTableConfiguration] = useState<TableConfiguration>({
        generalSearchbar: generalSearchbar || '',
        generalHeaders: generalHeaders || {},
        order: {},
        offset: 0,
        singleFieldsSearch: {},
        petitionNumber: 0
    })
    const [tableData, setTableData] = useState<{ [fieldName: string]: any }[] | null>(null)
    const saveTableDefaultFilterWhenChanges = useMemo(() => getSingleEmployeeParam('saveTableDefaultFilterWhenChanges') === 'on', [])

    const updateSelectedColumnsAndFilters = useCallback(async (values: TwinDictionary | null) => {
        const myName = 'table_' + name
        let isCustomFilter = values?.id && String(values?.id) !== '0' ? true : false
        if (!isCustomFilter && values && saveTableDefaultFilterWhenChanges) {
            delete values.id
            delete values.name
            const table = getSingleEmployeeParam(myName) || '{}'
            const myValuesStr = JSON.stringify(values)
            if (table !== myValuesStr) {
                await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: myValuesStr })
                changeEmployeeParams({ [myName]: myValuesStr })
            }
        }
    }, [saveTableDefaultFilterWhenChanges, name])

    const updateGeneralSeachBarEmployeeParams = useCallback(async (value: string) => {
        if (setSelectedColumnsAndFilters) {
            flushSync(async () => {
                let result = null
                await setSelectedColumnsAndFilters((old) => {
                    const copyOld: SelectedColumnsAndFilters = JSON.parse(JSON.stringify(old))
                    if (copyOld.generalSearchbar === value) {
                        return copyOld
                    }
                    const myResult = { ...copyOld, generalSearchbar: value }
                    result = myResult
                    return myResult
                })
                updateSelectedColumnsAndFilters(result)
            })
        }
    }, [updateSelectedColumnsAndFilters, setSelectedColumnsAndFilters])
    
    const updateGeneralHeadersEmployeeParams = useCallback(async (key: string, value: string) => {
        if (setSelectedColumnsAndFilters) {
            flushSync(async () => {
                let result = null
                await setSelectedColumnsAndFilters((old) => {
                    const copyOld: SelectedColumnsAndFilters = JSON.parse(JSON.stringify(old))
                    if (copyOld.generalHeaders?.[key] === value) {
                        return copyOld
                    }
                    if(!copyOld.generalHeaders){
                        copyOld.generalHeaders = {}
                    }
                    if(!value){
                        delete copyOld.generalHeaders[key]    
                    }else{
                        copyOld.generalHeaders[key] = value
                    }
                    const myResult = { ...copyOld }
                    result = myResult
                    return myResult
                })
                updateSelectedColumnsAndFilters(result)
            })
        }
    }, [updateSelectedColumnsAndFilters, setSelectedColumnsAndFilters])

    const updateSingleFieldEmployeeParams = useCallback(async (fieldName: string, value: string | string[]) => {
        if (setSelectedColumnsAndFilters) {
            flushSync(async () => {
                let result = null
                await setSelectedColumnsAndFilters((old) => {
                    const copyOld: SelectedColumnsAndFilters = JSON.parse(JSON.stringify(old))
                    let filters = copyOld?.filters || {}
                    if (filters) {
                        if (value === '' || (Array.isArray(value) && value[0] === '')) {
                            delete filters[fieldName]
                        } else {
                            filters[fieldName] = { value: value }
                        }
                    }
                    const myResult = { ...copyOld, filters }
                    result = myResult
                    return myResult
                })
                updateSelectedColumnsAndFilters(result)
            })
        }
    }, [updateSelectedColumnsAndFilters, setSelectedColumnsAndFilters])

    const updateOrderEmployeeParams = useCallback(async (value: string) => {
        if (setSelectedColumnsAndFilters) {
            flushSync(async () => {
                let result = null
                await setSelectedColumnsAndFilters((old) => {
                    const copyOld: SelectedColumnsAndFilters = JSON.parse(JSON.stringify(old))
                    let order = copyOld?.order || {}
                    if (order) {
                        const newOrder = getNextOrder(value, order, defaultOrder?.order || 'desc')
                        if (newOrder[value] === null) {
                            delete newOrder[value]
                        }
                        order = newOrder
                    }
                    const myResult = { ...copyOld, order }
                    result = myResult
                    return myResult
                })
                updateSelectedColumnsAndFilters(result)
            })
        }
    }, [defaultOrder?.order, updateSelectedColumnsAndFilters, setSelectedColumnsAndFilters])

    const changeGeneralSearchbar = useCallback(async(value: string) => {
        flushSync(async () => {
            let mustUpdate = true
            await setTableConfiguration((oldTableConfiguration) => {
                const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
                if (copyOld.generalSearchbar === value) {
                    mustUpdate = false
                }
                return {
                    ...copyOld,
                    offset: 0,
                    generalSearchbar: value
                }
            })
            if (mustUpdate) {
                updateGeneralSeachBarEmployeeParams(value)
            }
        })
    }, [setTableConfiguration, updateGeneralSeachBarEmployeeParams])

    const changeGeneralHeaders = useCallback(async (key: string, value: string) => {
        flushSync(async () => {
            let mustUpdate = true
            await setTableConfiguration((oldTableConfiguration) => {
                const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
                if (copyOld.generalHeaders?.[key] === value) {
                    mustUpdate = false
                }
                if (!copyOld.generalHeaders) {
                    copyOld.generalHeaders = {}
                }
                if (!value) {
                    delete copyOld.generalHeaders[key]
                } else {
                    copyOld.generalHeaders[key] = value
                }
                return {
                    ...copyOld,
                    offset: 0,
                }
            })
            if (mustUpdate) {
                updateGeneralHeadersEmployeeParams(key, value)
            }
        })
    }, [setTableConfiguration, updateGeneralHeadersEmployeeParams])

    const changeOrder = useCallback((fieldName: string) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            return {
                ...copyOld,
                offset: 0,
            }
        })
        updateOrderEmployeeParams(fieldName)
    }, [setTableConfiguration, updateOrderEmployeeParams])

    const changeOffset = useCallback((offset: number) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            return {
                ...copyOld,
                offset
            }
        })
    }, [setTableConfiguration])
    const changeSingleField = useCallback((fieldName: string, value: string | string[]) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            if ((Array.isArray(value) && value[0] === '')) {
                value = ''
            }
            return {
                ...copyOld,
                offset: 0
            }
        })
        updateSingleFieldEmployeeParams(fieldName, value)
    }, [setTableConfiguration, updateSingleFieldEmployeeParams])

    const changeOffsetVirtualTable = useCallback((params: ScrollEventData) => {
        const nitems = params.clientHeight / DEFAULT_ROWHEIGHT_PX
        const sub = DEFAULT_ROWHEIGHT_PX * nitems + params.scrollTop
        const actual = sub / DEFAULT_ROWHEIGHT_PX
        if (tableData) {
            const should_update = tableData.length - 10
            if ((maxOffset === undefined || maxOffset > tableConfiguration.offset) && should_update <= actual && tableData.length === tableConfiguration.offset + DEFAULT_LIMIT_ROWS) {
                changeOffset(tableConfiguration.offset + DEFAULT_LIMIT_ROWS)
            }
        }
    }, [tableConfiguration.offset, tableData, changeOffset, maxOffset])

    const myColumnsJustForEffect = JSON.stringify(columns)
    const myExtraParamsJustForEffect = JSON.stringify(extraWhereParams)
    const myExtraSubstituibleParamsJustForEffect = JSON.stringify(extraWhereSubstituibleParams)
    const myTableConfigurationJustForEffect = JSON.stringify(tableConfiguration)
    const myFiltersJustForEffect = JSON.stringify(filters || {})
    const myOrderJustForEffect = JSON.stringify(order || {})

    const updateDataTable = useCallback(async () => {
        const myColumns = JSON.parse(myColumnsJustForEffect)
        const myExtraWhereParams = JSON.parse(myExtraParamsJustForEffect || '{}')
        const myExtraSubstituibleWhereParams = JSON.parse(myExtraSubstituibleParamsJustForEffect || '{}')
        const myTableConfiguration = JSON.parse(myTableConfigurationJustForEffect || '{}')
        const myFiltersParsed = JSON.parse(myFiltersJustForEffect || '{}')
        const myOrderParsed = JSON.parse(myOrderJustForEffect || '{}')
        const whereBySearchBars = buildWhereBySearchbars(
            { generalSearchbar: myTableConfiguration.generalSearchbar, singleFieldsSearch: myTableConfiguration.singleFieldsSearch, filtersFieldsSearch: myFiltersParsed },
            myColumns
        )
        const whereByGeneralHeaders = getWhereFromGeneralHeaders(myTableConfiguration.generalHeaders)
        const orderBuiled = buildOrder({ ...myOrderParsed, ...myTableConfiguration.order }, myColumns)

        let apiTableData = await twinFetchPostJSON(getDataFrom, {
            limit: DEFAULT_LIMIT_ROWS,
            offset: myTableConfiguration.offset,
            where: {
                ...myExtraSubstituibleWhereParams,
                ...whereBySearchBars,
                ...myExtraWhereParams
            },
            specialWhere: {
                ...whereByGeneralHeaders
            },
            order: orderBuiled.length ? orderBuiled : [[defaultOrder?.defaultOrderField || 'id', defaultOrder?.order || 'desc']],
        })

        if (parseData) {
            apiTableData = parseData(apiTableData)
        }

        saveTableData(apiTableData, myTableConfiguration.offset, setTableData)

    }, [myTableConfigurationJustForEffect, getDataFrom, myColumnsJustForEffect, myExtraParamsJustForEffect, setTableData, defaultOrder?.defaultOrderField, defaultOrder?.order, parseData, myFiltersJustForEffect, myOrderJustForEffect, myExtraSubstituibleParamsJustForEffect])

    const getTableDataFromStart = useCallback((resetSearchbar?: boolean) => {
        setTableConfiguration((oldTableConfiguration) => {
            return JSON.parse(JSON.stringify({
                ...oldTableConfiguration,
                petitionNumber: oldTableConfiguration.petitionNumber + 1,
                generalSearchbar: resetSearchbar ? '' : oldTableConfiguration.generalSearchbar,
                offset: 0
            }))
        })
    }, [setTableConfiguration])

    useEffect(() => {
        const tableBody = document.querySelector('.ReactVirtualized__Table__Grid');
        if (tableBody && tableConfiguration.offset === 0) {
            tableBody.scrollTop = 0
        }
    }, [tableConfiguration.offset, tableConfiguration.petitionNumber])

    useEffect(() => {
        updateDataTable()
    }, [updateDataTable])

    useEffect(() => {
        setTableInstance?.({
            getTableDataFromStart,
            changeOffset
        })
    }, [setTableInstance, getTableDataFromStart, changeOffset])

    return { tableData, tableConfiguration, changeGeneralSearchbar, changeOffsetVirtualTable, headerChanges: { changeOrder, changeSingleField }, setTableConfiguration, saveTableDefaultFilterWhenChanges, getTableDataFromStart, changeGeneralHeaders }
}

export interface TwinTableLinkProps extends TableProps {
    createRowDataLink: CreateLinkProps
}

export const TwinTableLink: React.FC<TwinTableLinkProps> = ({ createRowDataLink, children, ...rest }) => {
    const createRowDataLinkStr = JSON.stringify(createRowDataLink)
    const createLinkCallback: CreateLinkFunctionType = useCallback((allRowData) => {
        return createLink(JSON.parse(createRowDataLinkStr), allRowData)
    }, [createRowDataLinkStr])
    return (
        <TwinTableLinkBase createRowDataLinkFunction={createLinkCallback} {...rest}>
            {children}
        </TwinTableLinkBase>
    )
}

export interface TwinTableLinkBaseProps extends TableProps {
    createRowDataLinkFunction: CreateLinkFunctionType
}
export const TwinTableLinkBase: React.FC<TwinTableLinkBaseProps> = ({ createRowDataLinkFunction, children, ...rest }) => {
    const rowRenderer = rest.rowRenderer
    const memorizedRowRenderer: TableRowRenderer = useCallback((props) => {
        if (Object.keys(props.rowData).length) {
            return <Link to={createRowDataLinkFunction(props.rowData)} key={props.key}>{rowRenderer ? rowRenderer(props) : defaultRowRenderer(props)}</Link>
        }
        return defaultRowRenderer(props)
    }, [createRowDataLinkFunction, rowRenderer])
    return (<TwinTable {...rest} rowRenderer={memorizedRowRenderer} >{children}</TwinTable>)
}

export const TwinTableLinkSimpleTable: React.FC<TwinTableLinkProps> = ({ createRowDataLink, children, ...rest }) => {
    const createRowDataLinkStr = JSON.stringify(createRowDataLink)
    const createLinkCallback: CreateLinkFunctionType = useCallback((allRowData) => {
        return createLink(JSON.parse(createRowDataLinkStr), allRowData)
    }, [createRowDataLinkStr])
    return (
        <TwinTableLinkSimpleTableBase createRowDataLinkFunction={createLinkCallback} {...rest}>
            {children}
        </TwinTableLinkSimpleTableBase>
    )
}

export const TwinTableLinkSimpleTableBase: React.FC<TwinTableLinkBaseProps> = ({ createRowDataLinkFunction, children, ...rest }) => {
    const rowRenderer = rest.rowRenderer
    const memorizedRowRenderer: TableRowRenderer = useCallback((props) => {
        if (Object.keys(props.rowData).length) {
            return <Link to={createRowDataLinkFunction(props.rowData)} key={props.key}>{rowRenderer ? rowRenderer(props) : defaultRowRenderer(props)}</Link>
        }
        return defaultRowRenderer(props)
    }, [createRowDataLinkFunction, rowRenderer])
    return (<TwinSimpleTable {...rest} rowRenderer={memorizedRowRenderer} >{children}</TwinSimpleTable>)
}

export default TwinTable